import React, { useEffect, useState } from 'react';
import { Bell, ChevronDown, X, Eye, EyeOff } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

interface HeaderProps { title: string; }
interface AuthUser { _id?: string; name: string; email: string; role: string; }

const API_BASE = getApiBase();

const PasswordStrength: React.FC<{ password: string }> = ({ password }) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const labels = ['Very Weak', 'Weak', 'Medium', 'Strong', 'Very Strong'];
  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#16a34a'];
  const i = Math.max(0, Math.min(score - 1, 4));
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ height: '4px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: '4px', width: `${(score / 5) * 100}%`, background: colors[i], transition: 'width 0.3s' }} />
      </div>
      <p style={{ margin: '4px 0 0', fontSize: '11px', color: colors[i] }}>{labels[i]}</p>
    </div>
  );
};

const inp: React.CSSProperties = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px 12px', fontSize: '14px', color: '#111827', outline: 'none', boxSizing: 'border-box', background: '#fafafa' };
const label: React.CSSProperties = { display: 'block', fontSize: '11px', fontWeight: 500, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' };

const Header: React.FC<HeaderProps> = ({ title }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [oldPass, setOldPass] = useState('');
  const [newPass1, setNewPass1] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('authUser');
    if (stored) { const p = JSON.parse(stored); setUser(p); setNewEmail(p.email); }
  }, []);

  useEffect(() => {
    const close = (e: MouseEvent) => { if (dropdownOpen) setDropdownOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [dropdownOpen]);

  const handleLogout = () => {
    localStorage.removeItem('authToken'); localStorage.removeItem('authUser');
    window.location.href = '/login';
  };

  const defaultHeaders = { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("authToken")}` };

  const updateEmail = async () => {
    if (!user?._id) return;
    try {
      const res = await fetch(`${API_BASE}/api/users/${user._id}`, { method: 'PUT', headers: defaultHeaders, body: JSON.stringify({ email: newEmail }) });
      const data = await res.json();
      if (!data.success) { alert(data.message || 'Failed to update email'); return; }
      const updated = { ...user, email: newEmail };
      setUser(updated); localStorage.setItem('authUser', JSON.stringify(updated));
      setShowEmailModal(false); alert('Email updated successfully.');
    } catch { alert('Server error while updating email.'); }
  };

  const updatePassword = async () => {
    if (!user?._id) return;
    if (newPass1 !== newPass2) { alert('New passwords do not match.'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/users/${user._id}/password`, { method: 'PUT', headers: defaultHeaders, body: JSON.stringify({ oldPassword: oldPass, newPassword: newPass1 }) });
      const data = await res.json();
      if (!data.success) { alert(data.message || 'Failed to update password'); return; }
      setShowPasswordModal(false); alert('Password updated successfully.');
      setOldPass(''); setNewPass1(''); setNewPass2('');
    } catch { alert('Server error while updating password.'); }
  };

  const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px', fontFamily: "'DM Sans', sans-serif" };
  const modal: React.CSSProperties = { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '400px', padding: '24px' };

  const initials = (user?.name || user?.email || 'A').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <header style={{ background: '#fff', borderBottom: '1px solid #f3f4f6', padding: '0 32px', height: '64px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'DM Sans', sans-serif", flexShrink: 0 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>{title}</h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Bell */}
          <button style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6b7280' }}>
            <Bell size={18} />
          </button>

          {/* User dropdown */}
          {user && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={e => { e.stopPropagation(); setDropdownOpen(d => !d); }}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fafafa', border: '1px solid #f3f4f6', borderRadius: '10px', padding: '6px 12px 6px 6px', cursor: 'pointer' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#fff5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, color: '#F1561D', flexShrink: 0 }}>
                  {initials}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: '#111827' }}>{user.email}</p>
                  <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', textTransform: 'capitalize' }}>{user.role}</p>
                </div>
                <ChevronDown size={14} color="#9ca3af" />
              </button>

              {dropdownOpen && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '180px', overflow: 'hidden' }}>
                  <button onClick={() => { setDropdownOpen(false); setShowEmailModal(true); }}
                    style={{ width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: '14px', color: '#374151', background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f9fafb' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                    Update Email
                  </button>
                  <button onClick={() => { setDropdownOpen(false); setShowPasswordModal(true); }}
                    style={{ width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: '14px', color: '#374151', background: 'none', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                    Update Password
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Logout */}
          <button onClick={handleLogout}
            style={{ background: '#F1561D', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
            Logout
          </button>
        </div>
      </header>

      {/* ── Email Modal ────────────────────────────────────────────────── */}
      {showEmailModal && (
        <div style={overlay} onClick={e => { if (e.target === e.currentTarget) setShowEmailModal(false); }}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Update Email</h3>
              <button onClick={() => setShowEmailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={18} /></button>
            </div>
            <label style={label}>New Email</label>
            <input type="email" style={inp} value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setShowEmailModal(false)} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              <button onClick={updateEmail} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#F1561D', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Password Modal ─────────────────────────────────────────────── */}
      {showPasswordModal && (
        <div style={overlay} onClick={e => { if (e.target === e.currentTarget) setShowPasswordModal(false); }}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Update Password</h3>
              <button onClick={() => setShowPasswordModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={label}>Current Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showOld ? 'text' : 'password'} style={{ ...inp, paddingRight: '40px' }} value={oldPass} onChange={e => setOldPass(e.target.value)} />
                  <button type="button" onClick={() => setShowOld(s => !s)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}>
                    {showOld ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label style={label}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showNew ? 'text' : 'password'} style={{ ...inp, paddingRight: '40px' }} value={newPass1} onChange={e => setNewPass1(e.target.value)} />
                  <button type="button" onClick={() => setShowNew(s => !s)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}>
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {newPass1 && <PasswordStrength password={newPass1} />}
              </div>
              <div>
                <label style={label}>Confirm New Password</label>
                <input type="password" style={{ ...inp, borderColor: newPass2 && newPass1 !== newPass2 ? '#fca5a5' : '#e5e7eb' }} value={newPass2} onChange={e => setNewPass2(e.target.value)} />
                {newPass2 && newPass1 !== newPass2 && <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#ef4444' }}>Passwords don't match</p>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setShowPasswordModal(false)} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              <button onClick={updatePassword} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#F1561D', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
